import { TICKS_PER_LOOP } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import React, { Dispatch, FC, SetStateAction, useEffect } from 'react'
import { useTreeState } from '../../../stores/Root'
import { Column } from '../../core/Components'
import Input from '../../core/Input'

const PLAYHEAD_OFFSET = '8px'
const GHOST_PLAYHEAD_OFFSET = '4px'

interface Props {
  instrumentLoopWidthPX: number
  setPlayheadCoords?: Dispatch<SetStateAction<{ x: number; x_offset: number }>>
  isPlayer?: boolean
  isGhostPlayhead?: boolean
}

const Playhead: FC<Props> = ({
  setPlayheadCoords,
  isPlayer,
  isGhostPlayhead,
  instrumentLoopWidthPX
}) => {
  const studio = isPlayer ? useTreeState().player : useTreeState().studio
  const playheadRef = React.useRef()
  const maxX = (studio.song.songDisplayLength + studio.extraBarsCount) * instrumentLoopWidthPX - 1
  const x = (instrumentLoopWidthPX * studio.playheadVisiblePositionInTicks) / TICKS_PER_LOOP
  const ghostPlayheadX = (instrumentLoopWidthPX * studio.ghostPlayheadTicks) / TICKS_PER_LOOP

  const movePlayhead = (e) => {
    const x = e.target.value
    const pos = (x / instrumentLoopWidthPX) * 64
    if (studio.repeatSection && (x / instrumentLoopWidthPX) * TICKS_PER_LOOP > studio.repeatEnd)
      return
    studio.userDraggingPlayhead(pos)
  }

  useEffect(() => {
    const playhead_curr: any = playheadRef.current
    if (!playhead_curr) return

    const slider_pos = playhead_curr.value / playhead_curr.max
    const pixel_position =
      playhead_curr.getBoundingClientRect().x + playhead_curr.clientWidth * slider_pos
    if (setPlayheadCoords)
      setPlayheadCoords({ x: pixel_position, x_offset: playhead_curr.clientWidth * slider_pos })
  }, [studio.playheadVisiblePositionInTicks])

  return (
    <Column
      style={{
        position: 'absolute',
        left: `-${isGhostPlayhead ? GHOST_PLAYHEAD_OFFSET : PLAYHEAD_OFFSET}`,
        width: `calc(${maxX}px + 2 * ${PLAYHEAD_OFFSET})`,
        height: '100%'
      }}
    >
      <Input
        ref={playheadRef}
        onChange={movePlayhead}
        onMouseUp={() => studio.disengageTimeline()}
        onMouseDown={() => studio.engageTimeline()}
        type="range"
        min="0"
        max={maxX}
        value={isGhostPlayhead ? ghostPlayheadX : x}
        className={isGhostPlayhead ? 'ghost-playhead' : 'playhead'}
        disabled={isGhostPlayhead}
        onClick={(e) => (e.target as any).blur()}
        css={{ width: '100%', height: '100%', position: 'absolute' }}
      />
    </Column>
  )
}

export default observer(Playhead)
